/**
 * TraceExplorerPane — React renderer for TraceExplorerPaneModel
 *
 * Columnar operational governance scan surface optimized for rapid
 * continuity and admissibility inspection.
 *
 * REFINEMENTS (V1.1):
 * - Columnar layout: STATUS → DURATION → TIME → ADMISSIBILITY → CONTINUITY → ACTOR → EVENT
 * - Compact, aligned, single-line rows for scanability
 * - Lightweight sorting support for duration, time, admissibility, continuity, status
 * - Sticky headers for column navigation
 * - Hover-highlight only (no selection motion)
 */

import React, { useState, useCallback, useMemo } from 'react';
import type { TraceExplorerPaneModel, TraceExplorerRow } from '../../modules/governanceEventStream/operationalRendering/types.js';
import { severityToColor, NEUTRAL, TYPOGRAPHY, ROW_HEIGHT, SPACING } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';
import { selectionRowStyle } from '../motion/selectionMotionSemantics.js';

interface TraceExplorerPaneProps {
  model: TraceExplorerPaneModel;
  onSelectEvent?: (eventId: string) => void;
  onToggleSpan?: (spanId: string) => void;
}

type SortField = 'duration' | 'time' | 'admissibility' | 'continuity' | 'status' | null;
type SortDirection = 'asc' | 'desc';
type ContinuityFilter = 'all' | 'stable' | 'degraded' | 'forked' | 'unresolved' | 'replayable';

const COLUMN_WIDTHS = {
  status: 70,
  duration: 80,
  time: 90,
  admissibility: 90,
  continuity: 90,
  actor: 140,
  event: 'auto',
} as const;

function getStatusColor(status: string): { bg: string; fg: string } {
  const s = status.toUpperCase();
  if (s === 'OK' || s === 'PASS') return { bg: '#0a2010', fg: '#5fa35f' };
  if (s === 'WARN' || s === 'DEGRADED') return { bg: '#201a10', fg: '#f5a35f' };
  if (s === 'REFUSED' || s === 'BLOCKED') return { bg: '#200a0a', fg: '#f55f5f' };
  if (s === 'RECOVERED' || s === 'STABILIZED') return { bg: '#102020', fg: '#5ff5a3' };
  if (s === 'PENDING') return { bg: '#201020', fg: '#f55fa3' };
  return { bg: NEUTRAL.bg400, fg: NEUTRAL.text400 };
}

function getAdmissibilityColor(admissibility: string): { bg: string; fg: string } {
  const a = admissibility.toUpperCase();
  if (a === 'ADMITTED') return { bg: '#0a2010', fg: '#5fa35f' };
  if (a === 'BOUNDED') return { bg: '#201a10', fg: '#f5a35f' };
  if (a === 'REFUSED') return { bg: '#200a0a', fg: '#f55f5f' };
  if (a === 'CANDIDATE') return { bg: '#201020', fg: '#f55fa3' };
  return { bg: NEUTRAL.bg400, fg: NEUTRAL.text400 };
}

function getContinuityColor(continuity: string): { bg: string; fg: string } {
  const c = continuity.toUpperCase();
  if (c === 'STABLE') return { bg: '#0a2010', fg: '#5fa35f' };
  if (c === 'DEGRADED') return { bg: '#201a10', fg: '#f5a35f' };
  if (c === 'RECOVERING') return { bg: '#102020', fg: '#5ff5a3' };
  if (c === 'FORKED') return { bg: '#201010', fg: '#f5f5a3' };
  if (c === 'UNRESOLVED') return { bg: '#200a0a', fg: '#f55f5f' };
  if (c === 'REPLAYABLE') return { bg: '#101020', fg: '#a35ff5' };
  return { bg: NEUTRAL.bg400, fg: NEUTRAL.text400 };
}

function formatDuration(ms?: number): string {
  if (ms === undefined || ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(0)}m`;
}

function formatCompactTime(timestamp: string): string {
  if (!timestamp) return '—';
  return timestamp.slice(11, 23);
}

function getRowStyle(row: TraceExplorerRow, isHovered: boolean): React.CSSProperties {
  const color = severityToColor(row.severity);
  const motionStyle = selectionRowStyle(row.isSelected, color.border);
  return {
    display: 'flex',
    alignItems: 'center',
    height: ROW_HEIGHT.compact.trace,
    paddingLeft: SPACING.sp3,
    paddingRight: SPACING.sp3,
    fontSize: TYPOGRAPHY.body.size,
    lineHeight: `${TYPOGRAPHY.body.lineHeight}px`,
    color: row.isSpanHeader ? NEUTRAL.text600 : NEUTRAL.text500,
    backgroundColor: row.isSelected ? NEUTRAL.bg500 : isHovered ? NEUTRAL.bg300 : 'transparent',
    cursor: 'pointer',
    fontWeight: row.isSpanHeader ? 600 : 400,
    fontFamily: 'system-ui, sans-serif',
    borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
    ...motionStyle,
  };
}

export const TraceExplorerPane: React.FC<TraceExplorerPaneProps> = ({ model, onSelectEvent, onToggleSpan }) => {
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [continuityFilter, setContinuityFilter] = useState<ContinuityFilter>('all');

  const sortedRows = useMemo(() => {
    if (!sortField) return model.rows;
    const sorted = [...model.rows];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'status':
          comparison = a.severity.localeCompare(b.severity);
          break;
        case 'time':
          comparison = a.timestamp.localeCompare(b.timestamp);
          break;
        case 'admissibility':
          comparison = (a.admissibility || 'UNVERIFIED').localeCompare(b.admissibility || 'UNVERIFIED');
          break;
        case 'continuity':
          comparison = (a.continuity || 'STABLE').localeCompare(b.continuity || 'STABLE');
          break;
        case 'duration':
          comparison = 0;
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [model.rows, sortField, sortDirection]);

  const filteredRows = useMemo(() => {
    if (continuityFilter === 'all') return sortedRows;
    return sortedRows.filter(row => {
      const continuity = (row.continuity || 'STABLE').toUpperCase();
      switch (continuityFilter) {
        case 'stable':
          return continuity === 'STABLE';
        case 'degraded':
          return continuity === 'DEGRADED';
        case 'forked':
          return continuity === 'FORKED';
        case 'unresolved':
          return continuity === 'UNRESOLVED';
        case 'replayable':
          return continuity === 'REPLAYABLE';
        default:
          return true;
      }
    });
  }, [sortedRows, continuityFilter]);

  const continuitySummary = useMemo(() => {
    const summary: Record<string, number> = {
      STABLE: 0,
      DEGRADED: 0,
      FORKED: 0,
      UNRESOLVED: 0,
      REPLAYABLE: 0,
      RECOVERING: 0,
    };
    model.rows.forEach(row => {
      const continuity = (row.continuity || 'STABLE').toUpperCase();
      if (summary[continuity] !== undefined) {
        summary[continuity]++;
      }
    });
    return summary;
  }, [model.rows]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  const handleRowClick = useCallback((row: TraceExplorerRow) => {
    if (row.isSpanHeader && row.spanId) {
      onToggleSpan?.(row.spanId);
    } else if (row.eventId) {
      onSelectEvent?.(row.eventId);
    }
  }, [onSelectEvent, onToggleSpan]);

  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Trace explorer error: {model.error}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        fontSize: TYPOGRAPHY.heading.size,
        fontWeight: TYPOGRAPHY.heading.weight,
        color: NEUTRAL.text600,
        backgroundColor: NEUTRAL.bg600,
        height: ROW_HEIGHT.compact.spanHeader,
      }}>
        <span>Trace Explorer</span>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
          {model.totalSpans} spans · {model.totalEvents} events
        </span>
      </div>

      {/* Continuity Summary Section */}
      <div style={{
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        backgroundColor: NEUTRAL.bg600,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        fontSize: TYPOGRAPHY.micro.size,
      }}>
        <div style={{ fontWeight: 700, color: NEUTRAL.text400, marginBottom: SPACING.sp1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Continuity Summary
        </div>
        <div style={{ display: 'flex', gap: SPACING.sp2, flexWrap: 'wrap' }}>
          {Object.entries(continuitySummary).map(([status, count]) => {
            if (count === 0) return null;
            const color = getContinuityColor(status);
            const isWarning = status === 'UNRESOLVED' || status === 'FORKED';
            return (
              <span
                key={status}
                onClick={() => setContinuityFilter(status.toLowerCase() as ContinuityFilter)}
                style={{
                  color: color.fg,
                  backgroundColor: color.bg,
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontWeight: isWarning ? 700 : 400,
                  cursor: 'pointer',
                  border: isWarning ? `1px solid ${color.fg}` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {status}: {count}
                {isWarning && <span style={{ fontSize: '10px' }}>⚠</span>}
              </span>
            );
          })}
          <span
            onClick={() => setContinuityFilter('all')}
            style={{
              color: NEUTRAL.text400,
              backgroundColor: continuityFilter === 'all' ? NEUTRAL.bg500 : 'transparent',
              padding: '2px 6px',
              borderRadius: 3,
              cursor: 'pointer',
            }}
          >
            All: {model.rows.length}
          </span>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Table Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: `${SPACING.sp1}px ${SPACING.sp3}px`,
          backgroundColor: NEUTRAL.bg600,
          borderBottom: `1px solid ${NEUTRAL.border}`,
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 700,
          color: NEUTRAL.text400,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          flexWrap: 'wrap',
          gap: SPACING.sp1,
        }}>
          <div style={{ 
            width: COLUMN_WIDTHS.status, 
            flexShrink: 0, 
            cursor: 'pointer',
            minWidth: COLUMN_WIDTHS.status 
          }} onClick={() => handleSort('status')}>
            STATUS {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div style={{ 
            width: COLUMN_WIDTHS.duration, 
            flexShrink: 0, 
            textAlign: 'right', 
            cursor: 'pointer',
            minWidth: COLUMN_WIDTHS.duration 
          }} onClick={() => handleSort('duration')}>
            DURATION {sortField === 'duration' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div style={{ 
            width: COLUMN_WIDTHS.time, 
            flexShrink: 0, 
            cursor: 'pointer',
            minWidth: COLUMN_WIDTHS.time 
          }} onClick={() => handleSort('time')}>
            TIME {sortField === 'time' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div style={{ 
            width: COLUMN_WIDTHS.admissibility, 
            flexShrink: 0, 
            cursor: 'pointer',
            minWidth: COLUMN_WIDTHS.admissibility 
          }} onClick={() => handleSort('admissibility')}>
            ADMISSIBILITY {sortField === 'admissibility' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div style={{ 
            width: COLUMN_WIDTHS.continuity, 
            flexShrink: 0, 
            cursor: 'pointer',
            minWidth: COLUMN_WIDTHS.continuity 
          }} onClick={() => handleSort('continuity')}>
            CONTINUITY {sortField === 'continuity' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div style={{ 
            width: COLUMN_WIDTHS.actor, 
            flexShrink: 0,
            minWidth: COLUMN_WIDTHS.actor 
          }}>
            ACTOR
          </div>
          <div style={{ 
            flex: 1, 
            minWidth: 150 
          }}>
            EVENT
          </div>
        </div>

        {filteredRows.map((row) => (
          <div
            key={row.rowId}
            style={getRowStyle(row, hoveredRowId === row.rowId)}
            onMouseEnter={() => setHoveredRowId(row.rowId)}
            onMouseLeave={() => setHoveredRowId(null)}
            onClick={() => handleRowClick(row)}
          >
            {row.isSpanHeader && row.spanId && (
              <span style={{
                marginRight: SPACING.sp2,
                fontSize: TYPOGRAPHY.micro.size,
                color: NEUTRAL.text300,
                width: 12,
                textAlign: 'center',
              }}>
                {model.collapsedSpanIds.includes(row.spanId) ? '▶' : '▼'}
              </span>
            )}
            {!row.isSpanHeader && (
              <span style={{ width: 12, marginRight: SPACING.sp2 }} />
            )}

            <div style={{ width: COLUMN_WIDTHS.status, flexShrink: 0 }}>
              <span style={{
                fontSize: TYPOGRAPHY.micro.size,
                fontWeight: 700,
                color: getStatusColor(row.severity).fg,
                backgroundColor: getStatusColor(row.severity).bg,
                padding: '1px 4px',
                borderRadius: 3,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}>
                {row.severity}
              </span>
            </div>

            <div style={{ width: COLUMN_WIDTHS.duration, flexShrink: 0, textAlign: 'right', fontFamily: TYPOGRAPHY.mono.family, fontSize: TYPOGRAPHY.mono.size, color: NEUTRAL.text400 }}>
              {formatDuration(undefined)}
            </div>

            <div style={{ width: COLUMN_WIDTHS.time, flexShrink: 0, fontFamily: TYPOGRAPHY.mono.family, fontSize: TYPOGRAPHY.mono.size, color: NEUTRAL.text400 }}>
              {formatCompactTime(row.timestamp)}
            </div>

            <div style={{ width: COLUMN_WIDTHS.admissibility, flexShrink: 0 }}>
              {row.admissibility && (
                <span style={{
                  fontSize: TYPOGRAPHY.micro.size,
                  fontWeight: 700,
                  color: getAdmissibilityColor(row.admissibility).fg,
                  backgroundColor: getAdmissibilityColor(row.admissibility).bg,
                  padding: '1px 4px',
                  borderRadius: 3,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}>
                  {row.admissibility}
                </span>
              )}
            </div>

            <div style={{ width: COLUMN_WIDTHS.continuity, flexShrink: 0 }}>
              {row.continuity && (
                <span style={{
                  fontSize: TYPOGRAPHY.micro.size,
                  fontWeight: 700,
                  color: getContinuityColor(row.continuity).fg,
                  backgroundColor: getContinuityColor(row.continuity).bg,
                  padding: '1px 4px',
                  borderRadius: 3,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  border: (row.continuity.toUpperCase() === 'UNRESOLVED' || row.continuity.toUpperCase() === 'FORKED') ? `1px solid ${getContinuityColor(row.continuity).fg}` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}>
                  {row.continuity}
                  {(row.continuity.toUpperCase() === 'UNRESOLVED' || row.continuity.toUpperCase() === 'FORKED') && (
                    <span style={{ fontSize: '10px' }}>⚠</span>
                  )}
                </span>
              )}
            </div>

            <div style={{ width: COLUMN_WIDTHS.actor, flexShrink: 0, fontFamily: TYPOGRAPHY.mono.family, fontSize: TYPOGRAPHY.mono.size, color: NEUTRAL.text400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.actor || '—'}
            </div>

            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: TYPOGRAPHY.label.size, color: NEUTRAL.text500 }}>
              {row.label}
            </div>
          </div>
        ))}

        {filteredRows.length === 0 && (
          <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, textAlign: 'center' }}>
            No trace data
          </div>
        )}
      </div>
    </div>
  );
};
